import { config } from "./urls";

const buildQueryString = (params = {}) => {
	const entries = Object.entries(params).filter(([, value]) => {
		return value !== undefined && value !== null && value !== "";
	});

	if (entries.length === 0) {
		return "";
	}

	const queryString = entries
		.map(([key, value]) => {
			const normalizedValue = Array.isArray(value)
				? value.join(",")
				: value;

			return `${encodeURIComponent(key)}=${encodeURIComponent(
				normalizedValue,
			)}`;
		})
		.join("&");

	return `?${queryString}`;
};

const buildRequestUrl = (baseUrl = "", path = "", queryString = "") => {
	const normalizedBaseUrl = baseUrl.replace(/\/+$/, "");
	const normalizedPath = path.replace(/^\/+/, "");

	if (!normalizedBaseUrl) {
		return `/${normalizedPath}${queryString}`;
	}

	return `${normalizedBaseUrl}/${normalizedPath}${queryString}`;
};

const parseResponseBody = async (response) => {
	const contentType = response.headers.get("content-type") || "";

	if (response.status === 204) {
		return null;
	}

	if (contentType.includes("application/json")) {
		return response.json();
	}

	const text = await response.text();

	try {
		return text ? JSON.parse(text) : null;
	} catch {
		return text;
	}
};

const isObjectLike = (value) => value !== null && typeof value === "object";

const findNestedValue = (body, keyCandidates) => {
	if (!isObjectLike(body)) {
		return "";
	}

	const queue = [body];
	const seen = new Set();

	while (queue.length > 0) {
		const current = queue.shift();

		if (!isObjectLike(current) || seen.has(current)) {
			continue;
		}

		seen.add(current);

		for (const [key, value] of Object.entries(current)) {
			if (
				keyCandidates.includes(key) &&
				value !== undefined &&
				value !== null &&
				value !== ""
			) {
				return value;
			}

			if (isObjectLike(value)) {
				queue.push(value);
			}
		}
	}

	return "";
};

const extractErrorMessage = (body) => {
	if (typeof body === "string") {
		return body;
	}

	const directMessage = findNestedValue(body, [
		"message",
		"mensaje",
		"detail",
		"descripcion",
		"reason",
		"errorMessage",
		"responseMessage",
	]);

	if (directMessage) {
		return directMessage;
	}

	const errorPayload = findNestedValue(body, ["error"]);

	if (typeof errorPayload === "string") {
		return errorPayload;
	}

	if (Array.isArray(errorPayload)) {
		const messages = errorPayload
			.map((item) => item?.msg || item?.message || item?.mensaje)
			.filter(Boolean);

		if (messages.length > 0) {
			return messages.join(" | ");
		}
	}

	if (isObjectLike(errorPayload)) {
		return (
			errorPayload?.msg ||
			errorPayload?.message ||
			errorPayload?.mensaje ||
			""
		);
	}

	return "";
};

const buildRequestError = (response, responseBody) => {
	const fallbackMessage = `HTTP error! status: ${response.status}`;
	const message = extractErrorMessage(responseBody) || fallbackMessage;
	const error = new Error(message);

	error.status = response.status;
	error.body = responseBody;
	error.data = responseBody;
	error.code = getResponseCode(responseBody);
	error.rawResponse = response;
	error.rawMessage = responseBody;

	if (error.code === "LRL008" && typeof window !== "undefined") {
		window.dispatchEvent(new CustomEvent("app:session-expired"));
	}

	return error;
};

const getResponseCode = (body) => {
	if (typeof body === "string") return body;

	return (
		findNestedValue(body, [
			"code",
			"codigo",
			"statusCode",
			"responseCode",
			"errorCode",
			"resultCode",
		]) || ""
	);
};

export const request = async ({
	path,
	method = "",
	body,
	params,
	headers = {},
	baseUrl = config.apiBaseUrl || "",
}) => {
	const queryString = buildQueryString(params);
	const isFormData =
		typeof FormData !== "undefined" && body instanceof FormData;
	const response = await fetch(buildRequestUrl(baseUrl, path, queryString), {
		method,
		credentials: "include",
		headers: {
			Accept: "application/json",

			...(isFormData ? {} : { "Content-Type": "application/json" }),
			...headers,
		},
		body:
			body !== undefined
				? isFormData
					? body
					: JSON.stringify(body)
				: undefined,
	});

	const responseBody = await parseResponseBody(response);

	if (!response.ok) {
		throw buildRequestError(response, responseBody);
	}

	return responseBody;
};

export const getRequest = (path, params, headers) => {
	return request({
		path,
		method: "GET",
		params,
		headers,
	});
};

export const getPublicRequest = (path, params, headers) => {
	return request({
		path,
		method: "GET",
		params,
		headers,
		baseUrl: config.publicApiBaseUrl || "",
	});
};

export const getRequestWithBaseUrl = (path, params, headers, baseUrl) => {
	return request({
		path,
		method: "GET",
		params,
		headers,
		baseUrl,
	});
};

export const postRequest = (path, body, headers) => {
	return request({
		path,
		method: "POST",
		body,
		headers,
	});
};

export const putRequest = (path, body, headers) => {
	return request({
		path,
		method: "PUT",
		body,
		headers,
	});
};

export const patchRequest = (path, body, headers) => {
	return request({
		path,
		method: "PATCH",
		body,
		headers,
	});
};

export const deleteRequest = (path, body, headers) => {
	return request({
		path,
		method: "DELETE",
		body,
		headers,
	});
};
